import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Database, Warning } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate("/");
    } else {
      setError(result.error);
    }
  };

  return (
    <div
      className="relative flex h-screen w-screen items-center justify-center bg-white"
      data-testid="login-page"
    >
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMGdlb21ldHJpYyUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3ODMwNDA5OTF8MA&ixlib=rb-4.1.0&q=85')",
        }}
      />
      <div className="absolute inset-0 bg-white/80" />
      <div className="relative z-10 w-full max-w-sm rounded-sm border border-zinc-300 bg-white p-8">
        <div className="mb-6 flex flex-col items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-sm border border-zinc-300 bg-zinc-50">
            <Database size={20} weight="bold" className="text-blue-600" />
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-zinc-950">
            BetterHome Inventory
          </h1>
          <p className="text-xs uppercase tracking-[0.05em] text-zinc-500">
            FileMaker Data Console
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-testid="login-form">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.05em] text-zinc-500">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@betterhome.com"
              className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              data-testid="login-email-input"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="password" className="text-xs uppercase tracking-[0.05em] text-zinc-500">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="rounded-sm border-zinc-300 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              data-testid="login-password-input"
            />
          </div>

          {error && (
            <div
              className="flex items-center gap-2 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700"
              data-testid="login-error-message"
            >
              <Warning size={16} />
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="mt-2 rounded-sm bg-blue-600 text-white hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
          <Link
            to="/forgot-password"
            className="text-center text-sm text-zinc-500 hover:text-blue-600 hover:underline"
            data-testid="forgot-password-link"
          >
            Forgot password?
          </Link>
        </form>
      </div>
    </div>
  );
}
